import { SearchOutlined } from "@ant-design/icons";
import { Button, Card, Select, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import type { UnitListFilters } from "~/types/unit";
import { ROUTES } from "~/utils";
import {
  countActiveListingFilters,
  listingFiltersToSearchParams,
} from "~/utils/listingFilterUrl";
import { normalizeLocationTerms, appendPendingLocationTerm } from "~/utils/listingLocations";
import { ListingFilterDrawer, ListingFilterTriggerButton } from "./ListingFilterDrawer";

const { Title, Text } = Typography;

export type LocationSearchBarMode = "navigate" | "inline";

export type LocationSearchBarProps = {
  title?: string;
  subtitle?: string;
  mode?: LocationSearchBarMode;
  /** Seed location chips (e.g. from URL on the units page). */
  initialLocations?: string[];
  /** Seed advanced filters from URL (inline mode). */
  initialAdvancedFilters?: UnitListFilters;
  /** Inline mode: update browse URL when Search is pressed. */
  onSearch?: (locations: string[], advancedFilters: UnitListFilters) => void;
  searchLoading?: boolean;
};

function stripNonAdvancedFilters(filters: UnitListFilters): UnitListFilters {
  const {
    propertyId: _propertyId,
    locations: _locations,
    location: _location,
    ...rest
  } = filters;
  return rest;
}

export function LocationSearchBar({
  title,
  subtitle,
  mode = "navigate",
  initialLocations = [],
  initialAdvancedFilters = {},
  onSearch,
  searchLoading = false,
}: LocationSearchBarProps) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<string[]>(() =>
    normalizeLocationTerms(initialLocations)
  );
  const [searchValue, setSearchValue] = useState("");
  const [advancedFilters, setAdvancedFilters] = useState<UnitListFilters>(() =>
    stripNonAdvancedFilters(initialAdvancedFilters)
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [locationError, setLocationError] = useState(false);

  const initialLocationsKey = initialLocations.join("\u001f");
  const initialAdvancedKey = JSON.stringify(
    stripNonAdvancedFilters(initialAdvancedFilters)
  );

  useEffect(() => {
    setLocations(normalizeLocationTerms(initialLocations));
  }, [initialLocationsKey]);

  useEffect(() => {
    setAdvancedFilters(stripNonAdvancedFilters(initialAdvancedFilters));
  }, [initialAdvancedKey]);

  const advancedFilterCount = countActiveListingFilters(advancedFilters, {
    excludePropertyId: true,
    excludeLocation: true,
  });

  const commitPendingInput = (current: string[]): string[] => {
    const next = appendPendingLocationTerm(current, searchValue);
    if (next.length !== current.length || searchValue.trim()) {
      setSearchValue("");
      setLocations(next);
    }
    return next;
  };

  const handleSearch = () => {
    const terms = commitPendingInput(locations);
    if (terms.length === 0) {
      setLocationError(true);
      return;
    }
    setLocationError(false);

    const filters: UnitListFilters = {
      ...advancedFilters,
      locations: terms,
    };

    if (mode === "inline") {
      onSearch?.(terms, advancedFilters);
      return;
    }

    const params = listingFiltersToSearchParams(filters);
    navigate(`${ROUTES.unit}?${params.toString()}`);
  };

  const handleApplyAdvanced = (next: UnitListFilters) => {
    const stripped = stripNonAdvancedFilters(next);
    setAdvancedFilters(stripped);
    if (mode === "inline") {
      onSearch?.(commitPendingInput(locations), stripped);
    }
  };

  return (
    <section style={{ width: "100%" }}>
      {title ? (
        <div style={{ marginBottom: 12 }}>
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
          {subtitle ? (
            <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
              {subtitle}
            </Text>
          ) : null}
        </div>
      ) : null}

      <Card
        styles={{ body: { padding: 16 } }}
        style={{
          width: "100%",
          background: "#fafafa",
          borderColor: "#f0f0f0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <Select
              mode="tags"
              size="large"
              open={false}
              suffixIcon={<SearchOutlined />}
              placeholder={t("Listing_search_location_placeholder")}
              value={locations}
              searchValue={searchValue}
              onSearch={setSearchValue}
              onChange={(next) => {
                setLocations(normalizeLocationTerms(next));
                if (locationError && next.length > 0) {
                  setLocationError(false);
                }
              }}
              onInputKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSearch();
                }
              }}
              tokenSeparators={[","]}
              status={locationError ? "error" : undefined}
              style={{ width: "100%" }}
              aria-label={t("Listing_search_location_placeholder")}
            />
            {locationError ? (
              <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                {t("Home_search_location_required")}
              </Text>
            ) : (
              <Text type="secondary" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                {t("Listing_search_add_location_hint")}
              </Text>
            )}
          </div>
          <Button
            type="primary"
            size="large"
            onClick={handleSearch}
            loading={searchLoading}
          >
            {t("Search")}
          </Button>
          <ListingFilterTriggerButton
            activeCount={advancedFilterCount}
            onOpen={() => setDrawerOpen(true)}
            size="large"
          />
        </div>
      </Card>

      <ListingFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={advancedFilters}
        onApply={handleApplyAdvanced}
      />
    </section>
  );
}
